import type { LiveChatMode } from '@/features/live/ablySessionChannels';
import type {
  EvergreenTestSnapshot,
  EvergreenTestTimelineEvent,
  ParticipationStatusCriteria,
  ParticipationStatusDto,
} from '@/features/webinar/types';
import { shouldHideDisplayedInteraction } from '@/lib/evergreenInteractionSchedule';
import type { PurchaseNotificationPayload } from '@/lib/purchaseNotificationDisplay';

/**
 * Rejoue en local la timeline d'un Evergreen, avec les règles de l'orchestrateur du back et du
 * spectateur réel : un événement que la vraie session ignorerait est ignoré ici aussi.
 */

// Bornes du back (ChatMessage) : au-delà, le message n'est jamais publié.
export const EVERGREEN_TEST_MESSAGE_MAX_LENGTH = 2000;
export const EVERGREEN_TEST_AUTHOR_MAX_LENGTH = 160;

/** Au-delà, l'écart entre deux positions est un saut (reprise, recalage) et non une lecture. */
export const EVERGREEN_TEST_MAX_PLAYBACK_STEP_MS = 2000;

const LIVE_CHAT_MODES: readonly LiveChatMode[] = ['PUBLIC', 'PRIVATE', 'DISABLED'];

const STATUS_CRITERIA: readonly ParticipationStatusCriteria[] = [
  'PRESENTER',
  'MODERATOR',
  'FIRST_SCORE',
  'SECOND_SCORE',
  'THIRD_SCORE',
  'TOP_25_PERCENT',
  'TOP_50_PERCENT',
  'TOP_75_PERCENT',
  'TOP_100_PERCENT',
  'CONVERSION',
];

export type EvergreenTestInteractionType = 'OFFER' | 'SURVEY' | 'QUIZ';

const INTERACTION_RESOURCE_TYPES: Record<EvergreenTestInteractionType, string> = {
  OFFER: 'OFFER_TEMPLATE',
  SURVEY: 'SURVEY_TEMPLATE',
  QUIZ: 'QUIZ_TEMPLATE',
};

/** Message rejoué, au format du message Ably que reçoit le vrai spectateur. */
export type EvergreenTestChatMessageData = {
  id: number;
  messageId: number;
  text: string;
  userName: string;
  teamMember: boolean;
  role: string;
  statusLabel: string | null;
  statusColor: string | null;
  statusIcon: string | null;
  repliedToMessageId: number | null;
  evergreen: true;
  privateMessage: boolean;
};

export type EvergreenTestChatMessage = {
  id: number;
  atMs: number;
  data: EvergreenTestChatMessageData;
};

type TimelineAction =
  | { kind: 'message'; atMs: number; message: EvergreenTestChatMessage }
  | { kind: 'pin'; atMs: number; messageId: number; pinned: boolean }
  | { kind: 'reaction'; atMs: number; messageId: number; emoji: string }
  | { kind: 'chatMode'; atMs: number; chatMode: LiveChatMode }
  | {
      kind: 'interaction';
      atMs: number;
      type: EvergreenTestInteractionType;
      resourceId: number;
      show: boolean;
    }
  | { kind: 'resource'; atMs: number; resourceId: number; show: boolean };

/** Événements ponctuels : ils ne laissent pas d'état, on ne les voit qu'en les traversant. */
type TimelineMoment =
  | { kind: 'purchase'; atMs: number; payload: PurchaseNotificationPayload }
  | { kind: 'liveReaction'; atMs: number; reaction: string };

export type EvergreenTestTimeline = {
  actions: TimelineAction[];
  moments: TimelineMoment[];
  initialChatMode: LiveChatMode;
  /** Premier SESSION_FINISH ou fin de la vidéo. */
  finishAtMs: number;
};

export type EvergreenTestState = {
  messages: EvergreenTestChatMessage[];
  pinnedMessageIds: number[];
  reactionsByMessageId: Record<number, Record<string, number>>;
  chatMode: LiveChatMode;
  offerId: number | null;
  surveyId: number | null;
  quizId: number | null;
  sharedResourceIds: number[];
};

export type EvergreenTestMoments = {
  purchaseNotifications: PurchaseNotificationPayload[];
  liveReactions: string[];
};

type JsonObject = Record<string, unknown>;

function isJsonObject(value: unknown): value is JsonObject {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/** `null` si le back lèverait une exception en lisant ce JSON (contenu absent ou invalide). */
function readJsonObject(raw: string | null): JsonObject | null {
  if (raw == null) return null;
  // Jackson lit un contenu vide comme un nœud absent : tous les champs prennent leur défaut.
  if (raw.trim() === '') return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    return isJsonObject(parsed) ? parsed : {};
  } catch {
    return null;
  }
}

/** Équivalent de `JsonNode.asText(fallback)`. */
function textOf(value: unknown, fallback: string): string;
function textOf(value: unknown, fallback: null): string | null;
function textOf(value: unknown, fallback: string | null): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value == null) return fallback;
  return '';
}

/** Équivalent de `JsonNode.asBoolean(fallback)`. */
function booleanOf(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
  }
  return fallback;
}

/** Équivalent de `JsonNode.asLong()`. */
function longOf(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) {
    return Number.parseInt(value.trim(), 10);
  }
  return 0;
}

function codePointLength(value: string): number {
  return [...value].length;
}

function resolveStatusCriteria(metadata: JsonObject): ParticipationStatusCriteria | null {
  const value = textOf(metadata.statusCriteria, null);
  return STATUS_CRITERIA.find((criteria) => criteria === value) ?? null;
}

function isStaffCriteria(criteria: ParticipationStatusCriteria | null): boolean {
  return criteria === 'PRESENTER' || criteria === 'MODERATOR';
}

function materializeMessage(
  timelineEvent: EvergreenTestTimelineEvent,
  materializedIds: ReadonlySet<number>,
  statuses: readonly ParticipationStatusDto[],
): EvergreenTestChatMessage | null {
  const { event } = timelineEvent;
  const content = event.payloadJson;
  if (content == null) return null;
  const metadata = readJsonObject(event.metadata);
  if (metadata == null) return null;
  const author = textOf(metadata.author, 'System');
  if (
    codePointLength(content) > EVERGREEN_TEST_MESSAGE_MAX_LENGTH ||
    codePointLength(author) > EVERGREEN_TEST_AUTHOR_MAX_LENGTH
  ) {
    return null;
  }

  const criteria = resolveStatusCriteria(metadata);
  const role = criteria === 'PRESENTER' || criteria === 'MODERATOR'
    ? criteria
    : textOf(metadata.role, 'USER');
  const teamMember = isStaffCriteria(criteria)
    ? true
    : booleanOf(metadata.teamMember, role !== 'ATTENDEE');
  const status = criteria
    ? statuses.find((candidate) => candidate.criteria === criteria)
    : undefined;

  let repliedToMessageId: number | null = null;
  if ('repliedToTimelineId' in metadata) {
    const parentId = longOf(metadata.repliedToTimelineId);
    if (materializedIds.has(parentId)) repliedToMessageId = parentId;
  }

  return {
    id: event.id,
    atMs: timelineEvent.effectiveStartOffsetMillis,
    data: {
      id: event.id,
      messageId: event.id,
      text: content,
      userName: author,
      teamMember,
      role,
      statusLabel: criteria ? (status?.label ?? null) : textOf(metadata.statusLabel, null),
      statusColor: criteria ? (status?.color ?? null) : textOf(metadata.statusColor, null),
      statusIcon: criteria ? (status?.icon ?? null) : textOf(metadata.statusIcon, null),
      repliedToMessageId,
      evergreen: true,
      privateMessage: booleanOf(metadata.privateMessage, false),
    },
  };
}

function readHideAction(payloadJson: string | null): boolean {
  if (!payloadJson) return false;
  try {
    const payload: unknown = JSON.parse(payloadJson);
    return isJsonObject(payload) && payload.action === 'HIDE';
  } catch {
    return false;
  }
}

function readReaction(payloadJson: string | null): string | null {
  const payload = readJsonObject(payloadJson);
  if (payload == null) return null;
  const reaction = textOf(payload.reaction, null);
  return reaction != null && reaction.trim().length > 0 ? reaction : null;
}

function readChatMode(payloadJson: string | null): LiveChatMode | null {
  const payload = readJsonObject(payloadJson);
  if (payload == null) return null;
  return LIVE_CHAT_MODES.find((mode) => mode === payload.chatMode) ?? null;
}

function readPurchaseNotification(payloadJson: string | null): PurchaseNotificationPayload | null {
  const payload = readJsonObject(payloadJson);
  return payload?.type === 'PURCHASE_NOTIFICATION' ? payload : null;
}

function readLiveReaction(payloadJson: string | null): string | null {
  const reaction = readJsonObject(payloadJson)?.reaction;
  return typeof reaction === 'string' && reaction.trim().length > 0 ? reaction.trim() : null;
}

function interactionTypeOf(timelineEvent: EvergreenTestTimelineEvent): EvergreenTestInteractionType | null {
  const { eventType, resourceType } = timelineEvent.event;
  if (eventType !== 'OFFER' && eventType !== 'SURVEY' && eventType !== 'QUIZ') return null;
  return INTERACTION_RESOURCE_TYPES[eventType] === resourceType ? eventType : null;
}

function isSharedResourceEvent(timelineEvent: EvergreenTestTimelineEvent): boolean {
  const { eventType, resourceType } = timelineEvent.event;
  return (
    (eventType === 'RESOURCE_SHOW' || eventType === 'RESOURCE_HIDE') &&
    resourceType === 'SHARED_RESOURCE'
  );
}

type ScheduledEntry = {
  atMs: number;
  // Un masquage de fin de créneau passe avant une apparition au même instant.
  phase: 0 | 1;
  sequence: number;
  timelineEvent: EvergreenTestTimelineEvent;
  scheduledHide: boolean;
};

function scheduleEntries(events: readonly EvergreenTestTimelineEvent[]): ScheduledEntry[] {
  const ordered = events
    .map((timelineEvent, index) => ({ timelineEvent, index }))
    .sort(
      (a, b) =>
        a.timelineEvent.effectiveStartOffsetMillis - b.timelineEvent.effectiveStartOffsetMillis ||
        a.timelineEvent.event.displayOrder - b.timelineEvent.event.displayOrder ||
        a.index - b.index,
    );
  const entries: ScheduledEntry[] = [];
  ordered.forEach(({ timelineEvent }, sequence) => {
    entries.push({
      atMs: timelineEvent.effectiveStartOffsetMillis,
      phase: 1,
      sequence,
      timelineEvent,
      scheduledHide: false,
    });
    const endMs = timelineEvent.effectiveEndOffsetMillis;
    if (endMs != null && (interactionTypeOf(timelineEvent) || isSharedResourceEvent(timelineEvent))) {
      entries.push({ atMs: endMs, phase: 0, sequence, timelineEvent, scheduledHide: true });
    }
  });
  return entries.sort((a, b) => a.atMs - b.atMs || a.phase - b.phase || a.sequence - b.sequence);
}

/**
 * Pré-calcule une fois la suite d'actions de la session. Les dépendances entre événements (réponse,
 * épinglage ou réaction sur un message déjà publié, changement de mode plus ancien) sont tranchées
 * ici, dans l'ordre où le back les exécuterait.
 */
export function buildEvergreenTestTimeline(
  snapshot: EvergreenTestSnapshot,
  statuses: readonly ParticipationStatusDto[] = [],
): EvergreenTestTimeline {
  const offerIds = new Set(snapshot.offers.map((offer) => offer.id));
  const surveyIds = new Set(snapshot.surveys.map((survey) => survey.id));
  const quizIds = new Set(snapshot.quizzes.map((quiz) => quiz.id));
  const readyResourceIds = new Set(
    snapshot.sharedResources
      .filter((resource) => resource.status === 'READY')
      .map((resource) => resource.id),
  );
  const templateIds: Record<EvergreenTestInteractionType, Set<number>> = {
    OFFER: offerIds,
    SURVEY: surveyIds,
    QUIZ: quizIds,
  };

  const actions: TimelineAction[] = [];
  const moments: TimelineMoment[] = [];
  const materializedIds = new Set<number>();
  let lastChatModeEventId: number | null = null;
  const durationMs = Math.max(0, snapshot.durationSeconds * 1000);
  let finishAtMs = durationMs;

  for (const entry of scheduleEntries(snapshot.events)) {
    const { atMs, timelineEvent } = entry;
    const { event } = timelineEvent;

    const interactionType = interactionTypeOf(timelineEvent);
    if (interactionType && event.resourceId != null) {
      const show = !entry.scheduledHide && !readHideAction(event.payloadJson);
      // Template introuvable : la vraie page garde l'affichage courant (requête en échec).
      if (show && !templateIds[interactionType].has(event.resourceId)) continue;
      actions.push({
        kind: 'interaction',
        atMs,
        type: interactionType,
        resourceId: event.resourceId,
        show,
      });
      continue;
    }

    if (isSharedResourceEvent(timelineEvent) && event.resourceId != null) {
      const show = !entry.scheduledHide && event.eventType === 'RESOURCE_SHOW';
      if (show && !readyResourceIds.has(event.resourceId)) continue;
      actions.push({ kind: 'resource', atMs, resourceId: event.resourceId, show });
      continue;
    }

    if (entry.scheduledHide) continue;

    switch (event.eventType) {
      case 'SUPPORT_MESSAGE': {
        const message = materializeMessage(timelineEvent, materializedIds, statuses);
        if (message) {
          materializedIds.add(message.id);
          actions.push({ kind: 'message', atMs, message });
        }
        break;
      }
      case 'MESSAGE_PIN':
      case 'MESSAGE_UNPIN': {
        if (event.resourceType !== 'INLINE' || event.resourceId == null) break;
        if (!materializedIds.has(event.resourceId)) break;
        actions.push({
          kind: 'pin',
          atMs,
          messageId: event.resourceId,
          pinned: event.eventType === 'MESSAGE_PIN',
        });
        break;
      }
      case 'CHAT_REACTION': {
        if (event.resourceType !== 'INLINE' || event.resourceId == null) break;
        if (!materializedIds.has(event.resourceId)) break;
        const emoji = readReaction(event.payloadJson);
        if (emoji) actions.push({ kind: 'reaction', atMs, messageId: event.resourceId, emoji });
        break;
      }
      case 'CHAT_MODE_CHANGED': {
        if (lastChatModeEventId != null && lastChatModeEventId >= event.id) break;
        const chatMode = readChatMode(event.payloadJson);
        if (!chatMode) break;
        lastChatModeEventId = event.id;
        actions.push({ kind: 'chatMode', atMs, chatMode });
        break;
      }
      case 'PURCHASE_NOTIFICATION': {
        const payload = readPurchaseNotification(event.payloadJson);
        if (payload) moments.push({ kind: 'purchase', atMs, payload });
        break;
      }
      case 'LIVE_REACTION': {
        if (event.resourceType !== 'INLINE') break;
        const reaction = readLiveReaction(event.payloadJson);
        if (reaction) moments.push({ kind: 'liveReaction', atMs, reaction });
        break;
      }
      case 'SESSION_FINISH':
        finishAtMs = Math.min(finishAtMs, Math.max(0, atMs));
        break;
      default:
        break;
    }
  }

  return { actions, moments, initialChatMode: snapshot.attendeeChatMode, finishAtMs };
}

/** Nombre d'actions déjà jouées à cette position (recherche dichotomique). */
export function evergreenTestActionCountAt(
  timeline: EvergreenTestTimeline,
  positionMs: number,
): number {
  const { actions } = timeline;
  let low = 0;
  let high = actions.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (actions[middle].atMs <= positionMs) low = middle + 1;
    else high = middle;
  }
  return low;
}

/** État affiché après les `actionCount` premières actions. */
export function evergreenTestStateAfter(
  timeline: EvergreenTestTimeline,
  actionCount: number,
): EvergreenTestState {
  const messages: EvergreenTestChatMessage[] = [];
  const pinned = new Set<number>();
  const reactionsByMessageId: Record<number, Record<string, number>> = {};
  const displayed: Record<EvergreenTestInteractionType, number | null> = {
    OFFER: null,
    SURVEY: null,
    QUIZ: null,
  };
  let chatMode = timeline.initialChatMode;
  let sharedResourceIds: number[] = [];

  const count = Math.min(Math.max(0, actionCount), timeline.actions.length);
  for (let index = 0; index < count; index += 1) {
    const action = timeline.actions[index];
    switch (action.kind) {
      case 'message':
        // Un message privé part sur le canal staff : le spectateur ne le voit pas.
        if (!action.message.data.privateMessage) messages.push(action.message);
        break;
      case 'pin':
        if (action.pinned) pinned.add(action.messageId);
        else pinned.delete(action.messageId);
        break;
      case 'reaction': {
        const forMessage = reactionsByMessageId[action.messageId] ?? {};
        forMessage[action.emoji] = (forMessage[action.emoji] ?? 0) + 1;
        reactionsByMessageId[action.messageId] = forMessage;
        break;
      }
      case 'chatMode':
        chatMode = action.chatMode;
        break;
      case 'interaction':
        if (action.show) displayed[action.type] = action.resourceId;
        else if (shouldHideDisplayedInteraction(displayed[action.type], action.resourceId)) {
          displayed[action.type] = null;
        }
        break;
      case 'resource':
        if (!action.show) {
          sharedResourceIds = sharedResourceIds.filter((id) => id !== action.resourceId);
        } else if (!sharedResourceIds.includes(action.resourceId)) {
          sharedResourceIds = [...sharedResourceIds, action.resourceId];
        }
        break;
    }
  }

  return {
    messages,
    pinnedMessageIds: messages.filter((message) => pinned.has(message.id)).map((m) => m.id),
    reactionsByMessageId,
    chatMode,
    offerId: displayed.OFFER,
    surveyId: displayed.SURVEY,
    quizId: displayed.QUIZ,
    sharedResourceIds,
  };
}

export function evergreenTestStateAt(
  timeline: EvergreenTestTimeline,
  positionMs: number,
): EvergreenTestState {
  return evergreenTestStateAfter(timeline, evergreenTestActionCountAt(timeline, positionMs));
}

export function isEvergreenTestFinishedAt(
  timeline: EvergreenTestTimeline,
  positionMs: number,
): boolean {
  return positionMs >= timeline.finishAtMs;
}

/**
 * Événements ponctuels franchis entre deux positions de lecture. Rien après un saut : un
 * spectateur qui arrive en retard ne reçoit pas les notifications déjà passées.
 */
export function crossedEvergreenTestMoments(
  timeline: EvergreenTestTimeline,
  fromMs: number,
  toMs: number,
): EvergreenTestMoments {
  const crossed: EvergreenTestMoments = { purchaseNotifications: [], liveReactions: [] };
  if (!(toMs > fromMs) || toMs - fromMs > EVERGREEN_TEST_MAX_PLAYBACK_STEP_MS) return crossed;
  const { moments } = timeline;
  let low = 0;
  let high = moments.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (moments[middle].atMs <= fromMs) low = middle + 1;
    else high = middle;
  }
  for (let index = low; index < moments.length && moments[index].atMs <= toMs; index += 1) {
    const moment = moments[index];
    if (moment.kind === 'purchase') crossed.purchaseNotifications.push(moment.payload);
    else crossed.liveReactions.push(moment.reaction);
  }
  return crossed;
}
